/**
 * Pure schedule-form logic: default values, validation and mapping to
 * NewMedicationSchedule. Separated from the form component so it is unit
 * testable without a DOM (contract §9 colocated tests).
 */
import type { HealthItemType, MedicationSchedule, NewMedicationSchedule, RecurrenceType } from '@/types'
import { todayLocalISODate } from '@/features/medication/dates'

export type ScheduleFormValues = {
  name: string
  healthType: HealthItemType
  dosage: string
  quantity: number
  instructions: string
  startDate: string
  endDate: string
  localTime: string
  timezone: string
  recurrenceType: RecurrenceType
  weekdays: number[]
  dayOfMonth: number
}

export type ScheduleFormErrors = Partial<
  Record<'name' | 'startDate' | 'endDate' | 'localTime' | 'weekdays', string>
>

export function emptyScheduleValues(): ScheduleFormValues {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam'
  return {
    name: '',
    healthType: 'medication',
    dosage: '',
    quantity: 1,
    instructions: '',
    startDate: todayLocalISODate(),
    endDate: '',
    localTime: '08:00',
    timezone: detected,
    recurrenceType: 'daily',
    weekdays: [1, 2, 3, 4, 5],
    dayOfMonth: 1,
  }
}

export function valuesFromSchedule(schedule: MedicationSchedule): ScheduleFormValues {
  return {
    name: schedule.name,
    healthType: schedule.healthType,
    dosage: schedule.dosage,
    quantity: schedule.quantity,
    instructions: schedule.instructions ?? '',
    startDate: schedule.startDate,
    endDate: schedule.endDate ?? '',
    localTime: schedule.localTime,
    timezone: schedule.timezone,
    recurrenceType: schedule.recurrenceType,
    weekdays: schedule.recurrenceRule.weekdays ?? [1, 2, 3, 4, 5],
    dayOfMonth: schedule.recurrenceRule.dayOfMonth ?? 1,
  }
}

export function validateScheduleValues(values: ScheduleFormValues): ScheduleFormErrors {
  const errors: ScheduleFormErrors = {}
  if (!values.name.trim()) errors.name = 'Vul een naam in.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(values.startDate)) errors.startDate = 'Kies een startdatum.'
  if (values.endDate && !/^\d{4}-\d{2}-\d{2}$/.test(values.endDate)) {
    errors.endDate = 'Kies een geldige einddatum.'
  } else if (values.endDate && values.endDate < values.startDate) {
    errors.endDate = 'De einddatum ligt vóór de startdatum.'
  }
  if (!/^\d{2}:\d{2}$/.test(values.localTime)) errors.localTime = 'Kies een tijdstip.'
  if (values.recurrenceType === 'weekly' && values.weekdays.length === 0) {
    errors.weekdays = 'Kies minimaal één weekdag.'
  }
  return errors
}

export function recurrenceRuleFromValues(values: ScheduleFormValues): NewMedicationSchedule['recurrenceRule'] {
  if (values.recurrenceType === 'weekly') {
    return { type: 'weekly', weekdays: [...values.weekdays].sort((a, b) => a - b) }
  }
  if (values.recurrenceType === 'monthly') {
    return { type: 'monthly', dayOfMonth: values.dayOfMonth }
  }
  return { type: values.recurrenceType }
}

export function valuesToNewSchedule(
  values: ScheduleFormValues,
  clientId: string,
  createdBy: string,
): NewMedicationSchedule {
  return {
    clientId,
    createdBy,
    name: values.name.trim(),
    healthType: values.healthType,
    dosage: values.dosage.trim(),
    quantity: values.quantity,
    instructions: values.instructions.trim() ? values.instructions.trim() : null,
    startDate: values.startDate,
    endDate: values.endDate ? values.endDate : null,
    localTime: values.localTime,
    timezone: values.timezone,
    recurrenceType: values.recurrenceType,
    recurrenceRule: recurrenceRuleFromValues(values),
    active: true,
  }
}
